import Contact from "./Contact";
import CallDisposition from "./CallDisposition";
import Deal from "./Deal";
import { CallFlow } from "@/model/interface/CallFlow";
import PhoneNumber from "@/model/interface/PhoneNumber";
import CallRecording from "./CallRecording";
import Membership from "./Membership";
import { RecordingState } from "@/enum/RecordingState";
import { DevicePlatform } from "@/enum/DevicePlatform";

export enum CallDirection {
  INBOUND = "INBOUND",
  OUTBOUND = "OUTBOUND",
}

export enum CallStatus {
  INITIATED = "INITIATED",
  RINGING = "RINGING",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  BUSY = "BUSY",
  NO_ANSWER = "NO_ANSWER",
  CANCELED = "CANCELED",
  MISSED = "MISSED",
}

export interface CallStats {
  calls: number;
  conversations: number;
  talkTimeSeconds: number;
  missed: number;
  noDisposition: number;
  unreviewedRecordings: number;
  missedWithoutCallback: number;
}

export interface CallScript {
  id: string;
  name: string;
  body: string;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
}

export interface CallScriptRequest {
  name?: string;
  body?: string;
  isActive?: boolean;
  sortOrder?: number;
}

export interface CallScriptContext {
  firstName?: string | null;
  lastName?: string | null;
  company?: string | null;
  propertyAddress?: string | null;
  dealStage?: string | null;
  agentName?: string | null;
  businessName?: string | null;
}

export interface CallScriptToken {
  key: keyof CallScriptContext;
  label: string;
}

export interface CallScriptSegment {
  text: string;
  isToken: boolean;
  isResolved: boolean;
}

export default interface Call {
  id?: string;
  phoneNumber: string;
  direction: CallDirection;
  status: CallStatus;
  duration?: number;
  cost?: number;
  fromPhoneNumber?: PhoneNumber;
  callFlow?: CallFlow;
  contact?: Contact;
  deal?: Deal;
  disposition?: CallDisposition;
  callScript?: Pick<CallScript, "id" | "name"> | null;
  voicemailMessageName?: string | null;
  voicemailMessageSeconds?: number | null;
  notes?: string;
  carrierCallSid?: string;
  recording?: CallRecording | null;
  recordingState?: RecordingState;
  recordingNoticeAt?: string | null;
  recordingSkippedReason?: string | null;
  answeredBy?: string;
  handledByMembership?: Membership;
  platform?: DevicePlatform;
  createdAt?: string;
  answeredAt?: string;
  endedAt?: string;
  deletedAt?: string;
}

export type CallParticipantRole = "REMOTE" | "AGENT" | "EXTERNAL";

export type CallParticipantStatus = "RINGING" | "JOINED" | "LEFT";

export interface CallParticipant {
  id: string;
  role: CallParticipantRole;
  status: CallParticipantStatus;
  number?: string | null;
  joinedAt?: string | null;
  heldAt?: string | null;
  membership?: Membership | null;
}

export interface AddCallParticipantRequest {
  membership?: { id: string };
  number?: string;
}

export interface CallPlatformRequest {
  platform: DevicePlatform;
}

export type CallTransferStatus = "RINGING" | "ANSWERED" | "MISSED" | "ROUTED";

export interface CallTransfer {
  id: string;
  kind: "TRANSFER" | "ADD";
  status: CallTransferStatus;
  note?: string | null;
  targetNumber?: string | null;
  targetMembership?: Membership | null;
  fromMembership?: Membership | null;
  answeredByMembership?: Membership | null;
  createdAt: string;
  answeredAt?: string | null;
  endedAt?: string | null;
}

export interface CallTransferRequest {
  targetMembership?: { id: string };
  targetTeam?: { id: string };
  targetPhoneNumber?: { id: string };
  targetNumber?: string;
  note?: string;
}

export type CallTransferTargetKind = "MEMBER" | "TEAM" | "PHONE_NUMBER" | "NUMBER";

export interface CallTransferTarget {
  kind: CallTransferTargetKind;
  id?: string;
  number?: string;
  name: string;
}
